import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import {
  notificationWeeksValidationRule,
  taxCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import {
  getAgencyRefMethodOptions,
  getBankFileReferenceFieldOptions,
  defaultCandidateStatus,
  defaultNonEUVerifiedIdMethodOptions,
  defaultNonUkRightToWorkStatus,
  defaultNonUKVerifiedIdMethodOptions,
  defaultOnBoardingOptions,
  defaultPaymentFrequency,
  getPaymentMethodOptions,
  defaultPaymentTerm,
  defaultPRSIEeClassesOptions,
  defaultTaxBasisIrelandOptions,
  getTimeSheetImportValidationOptions,
  defaultUKVerifiedIdMethodOptions,
  NICategoryOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import {
  AgencyTimesheetValueAccessEnum,
  enumMarginMethod,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import {
  countryList,
  defaultMarginMethod,
  getAppConfig,
  getSalaryCalculationMethodOptions,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { isTableMarginMethod } from '@nxsys/utils/margin';
import { TableOutlined } from '@ant-design/icons';
import { Col, Form, Row } from 'antd';
import { some } from 'lodash';
import React, { Ref, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import MarginTableModal from '../../../agencies/components/MarginTableModal';

export type CompanyDefaultFormValues = {
  paymentTerm: Option<string> | null;
  taxCode: string;
  taxBasis: Option<string> | null;
  paymentFrequency: Option<string> | null;
  NICategory: Option<string> | null;
  prsiEeClasses: Option<string> | null;
  candidateStatus: Option<string> | null;
  paymentMethod: Option<string> | null;
  passProtectDoc: boolean | null;
  agencyRefMethod: Option<string> | null;
  marginMethod: Option<string> | null;
  margin: number | null;
  minMargin: number | null;
  marginCapped: number | null;
  autoReduceMarginForNMW: boolean | null;
  timesheetImportValidation: Option<string> | null;
  VATRegistered: boolean | null;
  isRestrictedHours: boolean | null;
  isRequiredAttachmentExpenses: boolean | null;
  invoicePrefix: string;
  invoiceNextNumber: string;
  creditNotePrefix: string;
  creditNoteNextNumber: string;
  enableAWRTracking: boolean | null;
  enableWTR: boolean | null;
  notificationWeeks: string;
  takeHomePayPercent: number | null;
  salaryCalMethod: Option<string> | null;
  hoursCap: number | null;
  isSameAsCompany: boolean | null;
  invoicingCompanyName: string;
  invoicingTradingName: string;
  invoicingAddress1: string;
  invoicingAddress2: string;
  invoicingAddress3: string;
  invoicingAddress4: string;
  invoicingPostCode: string;
  invoicingCity: string;
  invoicingCounty: string;
  invoicingCountry: Option<string> | null;
  invoicingCompanyNo: string;
  invoicingVATRegNo: string;
  enableBenefits: boolean | null;
  bankFileReferenceField: Option<string> | null;
  nonUkRightToWorkStatus: Option<string> | null;
  subOnboarding: boolean | null;
  UKVerifiedIdMethod: Option<string> | null;
  emailOnboardingNotification: string;
  nonUKVerifiedIdMethod: Option<string> | null;
  onboarding: string[];
};

export type CompanyDefaultFormRef = {
  submit: () => void;
};

type CompanyDefaultFormProps = {
  formRef?: Ref<CompanyDefaultFormRef>;
  onSubmit?: (values: CompanyDefaultFormValues) => void;
  defaultValues?: Partial<CompanyDefaultFormValues>;
  /** Company whose margin band table opens when the method is a table. */
  companyId?: string;
};

const CompanyDefaultForm: React.FC<CompanyDefaultFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  companyId,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const defaultPaymentModel = getAppConfig('paymentModels');

  const [form] = Form.useForm<CompanyDefaultFormValues>();
  const watchValues = Form.useWatch([], form);

  const onboardingOptions = useMemo(() => {
    return defaultOnBoardingOptions
      .filter((item) => {
        if (item.isBureau === false && isBureau) return false;
        if (item.isUk && isIreland) return false;
        if (item.isIreland && !isIreland) return false;
        return true;
      })
      .map((item) =>
        isIreland && item.value === 'ADD_NI_NUMBER'
          ? { ...item, label: 'Add PPS Number' }
          : item,
      );
  }, [isBureau, isIreland]);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: CompanyDefaultFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  const isSameAsCompany = watchValues?.isSameAsCompany;
  const subOnboarding = watchValues?.subOnboarding;
  const enableAWRTracking = watchValues?.enableAWRTracking;
  const showPayeFthFields = some(
    defaultPaymentModel,
    (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
  );
  const marginMethod = watchValues?.marginMethod;
  // Margin fields are GBP when Fixed Fee, % otherwise — pick the input purpose
  // by mode (suffix below follows the same switch).
  const MarginValueInput =
    marginMethod?.value === 'FIXED_FEE' ? AppCurrencyInput : AppRateInput;
  // A table method takes its amount from the company's band table, opened from
  // the icon beside the method (same as the agency and employee screens).
  const isTableMethod = isTableMarginMethod(marginMethod?.value);
  const [isMarginTableOpen, setIsMarginTableOpen] = useState(false);
  const toggleMarginTable = () => setIsMarginTableOpen((open) => !open);
  const marginSuffix = isTableMethod
    ? undefined
    : marginMethod?.value
      ? marginMethod?.value === 'FIXED_FEE'
        ? showCurrencyByCountry(payrollCountry)
        : '%'
      : undefined;

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      {...(defaultValues
        ? {
            initialValues: {
              agencyRefMethod: {
                label: isIreland ? 'PPS Number' : 'NI Number',
                value: 'NI_NUMBER',
              },
              timesheetImportValidation: {
                label: 'Agency Reference + Forename & Surname',
                value: 'AGENCY_REFERENCE_FORENAME_SURNAME',
              },
              isRestrictedHours: false,
              ...defaultValues,
            },
          }
        : {})}
    >
      <Row gutter={[16, 16]}>
        {!isBureau && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.paymentTerm']}
              name="paymentTerm"
            >
              <AppSelect
                options={defaultPaymentTerm}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        {!isIreland ? (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.taxCode']}
              name="taxCode"
              rules={[taxCodeValidationRule(messages)]}
            >
              <AppInput upperCaseAll placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
        ) : (
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.taxBasis']} name="taxBasis">
              <AppSelect
                options={defaultTaxBasisIrelandOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.paymentFrequency']}
            name="paymentFrequency"
          >
            <AppSelect
              options={defaultPaymentFrequency}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        {!isIreland ? (
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.niCategory']} name="NICategory">
              <AppSelect
                options={NICategoryOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        ) : (
          <Col sm={24} md={12}>
            <Form.Item label="PRSI EE Classes" name="prsiEeClasses">
              <AppSelect
                options={defaultPRSIEeClassesOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.candidateStatus']}
            name="candidateStatus"
          >
            <AppSelect
              options={defaultCandidateStatus}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        {!isBureau && !isIreland && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.paymentMethod']}
              name="paymentMethod"
            >
              <AppSelect
                options={getPaymentMethodOptions(isIreland)}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={`${messages['common.passwordProtectDocument']}?`}
            name="passProtectDoc"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={
              isBureau
                ? (messages['common.employerReference'] ?? 'Employer Reference')
                : (messages['common.agencyReference'] ?? 'Agency Reference')
            }
            name="agencyRefMethod"
          >
            <AppSelect
              options={getAgencyRefMethodOptions(isIreland)}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        {!isBureau && (
          <>
            <Col sm={24} md={12}>
              <Form.Item label={messages['common.marginMethod']}>
                <div className="flex gap-[8px]">
                  <Form.Item name="marginMethod" noStyle>
                    <AppSelect
                      options={defaultMarginMethod}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  {isTableMethod && companyId && (
                    <div className="flex items-center">
                      <TableOutlined
                        className="cursor-pointer"
                        aria-label={messages['common.marginMethod']}
                        onClick={toggleMarginTable}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>
              {isTableMethod && companyId && (
                <MarginTableModal
                  title={marginMethod?.label ?? ''}
                  isOpen={isMarginTableOpen}
                  toggle={toggleMarginTable}
                  companyId={companyId}
                  accessType={
                    marginMethod?.value ===
                    enumMarginMethod.PERCENT_TABLE_ON_TIMESHEET_VALUE
                      ? AgencyTimesheetValueAccessEnum.PERCENT
                      : AgencyTimesheetValueAccessEnum.AMOUNT
                  }
                />
              )}
            </Col>
            <Col sm={24} md={12}>
              <Form.Item label={messages['common.margin']} name="margin">
                <MarginValueInput
                  placeholder={messages['form.enter']}
                  step="0.01"
                  disabled={isTableMethod}
                  suffix={marginSuffix}
                />
              </Form.Item>
            </Col>
            {marginMethod?.value ===
              enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
              <Col sm={24} md={12}>
                <Form.Item
                  label={messages['common.miniumMargin']}
                  name="minMargin"
                >
                  <MarginValueInput
                    placeholder={messages['form.enter']}
                    step="0.01"
                    suffix={
                      marginMethod?.value
                        ? marginMethod?.value === 'FIXED_FEE'
                          ? showCurrencyByCountry(payrollCountry)
                          : '%'
                        : undefined
                    }
                  />
                </Form.Item>
              </Col>
            )}
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.marginCapped']}
                name="marginCapped"
              >
                {isTableMethod ? (
                  // The cap applies to the amount the band table yields.
                  <AppCurrencyInput
                    placeholder={messages['form.enter']}
                    step="0.01"
                    suffix={showCurrencyByCountry(payrollCountry)}
                  />
                ) : (
                  <MarginValueInput
                    placeholder={messages['form.enter']}
                    step="0.01"
                    suffix={marginSuffix}
                  />
                )}
              </Form.Item>
            </Col>
            {/* NX-5228: Margin auto-reduction runs only inside `payrollCountry !== IRELAND` in
                generateDraftPayrollJob; an Irish NMW breach raises an error instead. */}
            {!isIreland && (
              <Col sm={24} md={12}>
                <Form.Item
                  label={messages['common.autoReduceMarginForNMW']}
                  name="autoReduceMarginForNMW"
                >
                  <AppRadioGroup options={yesNoOptions} />
                </Form.Item>
              </Col>
            )}
          </>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.timesheetImportValidation']}
            name="timesheetImportValidation"
          >
            <AppSelect
              options={getTimeSheetImportValidationOptions(isBureau)}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        {!isBureau && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.vatRegistered']}
              name="VATRegistered"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {/* NX-5228: The 20-hour restricted-hours check only runs for UK payrolls, and only for a
            Tier 4 student visa (getIsRestrictedHoursValidation). Ireland's Stamp 2 rule
            differs (20 hours in term, 40 in holidays), so the UK check could not stand in
            for it either. */}
        {!isBureau && !isIreland && (
          <Col sm={24} md={12}>
            <Form.Item
              label={`${messages['common.isRestrictedHours']}?`}
              name="isRestrictedHours"
              tooltip={messages['common.restrictedHoursTooltip']}
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {!isBureau && (
          <Col sm={24} md={12}>
            <Form.Item
              label={`${messages['common.isRequiredAttachmentExpenses']}?`}
              name="isRequiredAttachmentExpenses"
              tooltip="Workers must attach a receipt before claiming an expense."
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {!isBureau && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.invoicePrefix']}
                name="invoicePrefix"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.invoiceNextNumber']}
                name="invoiceNextNumber"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.creditNotePrefix']}
                name="creditNotePrefix"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.creditNoteNextNumber']}
                name="creditNoteNextNumber"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            {/* NX-5228: the Agency Workers Regulations are UK legislation, so
                neither the toggle nor the notification-weeks field it reveals
                applies to an Irish employer. */}
            {!isIreland && (
              <Col sm={24} md={12}>
                <Form.Item
                  label={messages['common.enableAWRTracking']}
                  name="enableAWRTracking"
                >
                  <AppRadioGroup options={yesNoOptions} />
                </Form.Item>
              </Col>
            )}
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.enableWorkingTimeRegulationTracking']}
                name="enableWTR"
              >
                <AppRadioGroup options={yesNoOptions} />
              </Form.Item>
            </Col>
            {enableAWRTracking && !isIreland && (
              <Col sm={24} md={12}>
                <Form.Item
                  label={messages['common.notificationWeeks']}
                  name="notificationWeeks"
                  rules={[notificationWeeksValidationRule(messages)]}
                >
                  <AppInput
                    placeholder={
                      messages['common.notificationWeeksPlaceholder']
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </>
        )}
        {showPayeFthFields && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.takeHomePercent']}
                name="takeHomePayPercent"
              >
                <AppRateInput
                  placeholder={messages['form.enter']}
                  step="0.01"
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.salaryCalculationMethod']}
                name="salaryCalMethod"
              >
                <AppSelect
                  options={getSalaryCalculationMethodOptions()}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item label={messages['common.hoursCap']} name="hoursCap">
                <AppRateInput placeholder={messages['form.enter']} min={0} />
              </Form.Item>
            </Col>
          </>
        )}
        {!isBureau && (
          <Col sm={24} md={12}>
            <Form.Item
              label={`${messages['common.invoicingDetailsSameAsCompanyDetails']}?`}
              name="isSameAsCompany"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {!isBureau && !isSameAsCompany && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={
                  isBureau
                    ? messages['common.bureau.companyName']
                    : messages['common.companyName']
                }
                name="invoicingCompanyName"
                rules={[
                  { required: true, message: 'Please enter company name' },
                ]}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.tradingName']}
                name="invoicingTradingName"
                rules={[
                  { required: true, message: 'Please enter trading name' },
                ]}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${messages['common.address']} 1`}
                name="invoicingAddress1"
                rules={[{ required: true, message: 'Please enter address 1' }]}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${messages['common.address']} 2`}
                name="invoicingAddress2"
                rules={[{ required: true, message: 'Please enter address 2' }]}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${messages['common.address']} 3`}
                name="invoicingAddress3"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${messages['common.address']} 4`}
                name="invoicingAddress4"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={isIreland ? 'Eircode' : messages['common.postCode']}
                name="invoicingPostCode"
                rules={[
                  {
                    required: true,
                    message: isIreland
                      ? 'Please enter Eircode'
                      : 'Please enter post code',
                  },
                ]}
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.cityTown']}
                name="invoicingCity"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.county']}
                name="invoicingCounty"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.country']}
                name="invoicingCountry"
              >
                <AppSelect
                  options={countryList}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.companyNumber']}
                name="invoicingCompanyNo"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.vatRegNo']}
                name="invoicingVATRegNo"
                rules={[
                  {
                    pattern: isIreland ? /^IE\d{9}$/ : /^\d{9}$/,
                    message: 'VAT number must be exactly 9 digits',
                  },
                ]}
                {...(isIreland && {
                  normalize: (value: string) =>
                    value?.startsWith('IE') ? value : 'IE',
                  initialValue: 'IE',
                })}
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  maxLength={isIreland ? 11 : 9}
                />
              </Form.Item>
            </Col>
          </>
        )}
        {!isBureau && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.enableBenefits']}
              name="enableBenefits"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item label="Bank File Reference" name="bankFileReferenceField">
            <AppSelect
              options={getBankFileReferenceFieldOptions(isBureau)}
              placeholder={messages['form.select']}
              allowClear
            />
          </Form.Item>
        </Col>
        {/* NX-5228: "Non UK Right To Work" is a UK immigration status; Ireland
            runs employment permits under its own rules. Gated on product only,
            it showed on every Irish umbrella employer. */}
        {!isBureau && !isIreland && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.nonUKRightToWorkStatus']}
              name="nonUkRightToWorkStatus"
            >
              <AppSelect
                options={defaultNonUkRightToWorkStatus}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}

        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.employeeAndSubcontractorOnboarding']}
            name="subOnboarding"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </Col>
        {subOnboarding && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${
                  isIreland
                    ? messages['common.euNationals']
                    : messages['common.ukNationals']
                } - ${messages['common.noPassport']}`}
                name="UKVerifiedIdMethod"
              >
                <AppSelect
                  options={defaultUKVerifiedIdMethodOptions}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.onboardingEmailNotifications']}
                name="emailOnboardingNotification"
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  allowCapitalize={false}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={`${
                  isIreland
                    ? messages['common.nonEUNationals']
                    : messages['common.nonUKNationals']
                } - ${messages['common.noPassport']}`}
                name="nonUKVerifiedIdMethod"
              >
                <AppSelect
                  options={
                    isIreland
                      ? defaultNonEUVerifiedIdMethodOptions
                      : defaultNonUKVerifiedIdMethodOptions
                  }
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.onboarding']}
                name="onboarding"
              >
                <AppSelect
                  options={onboardingOptions}
                  placeholder={messages['form.select']}
                  mode="multiple"
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </AppFormContainer>
  );
};

export default CompanyDefaultForm;
